import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { PageHeaderBar } from '../../../components/layout/PageHeaderBar';
import { appShellHeaderTitleSx } from '../../../components/layout/layoutConstants';
import withComponentAuth from '../../../hoc/withComponentAuth';
import { dashboardTokens } from '../../Dashboard/Components/dashboardCardStyles';
import { DateRangePopover } from '../components/DateRangePopover';
import { AdminAvatar } from '../components/AdminAvatar';
import { platformMeta } from '../components/leadsMeta';
import { ConnectionCard } from './components/ConnectionCard';
import { MetaKpiTiles } from './components/MetaKpiTiles';
import { LeadPerformanceChart } from './components/LeadPerformanceChart';
import { TopCampaigns } from './components/TopCampaigns';
import { MetaCampaignsTable } from './components/MetaCampaignsTable';
import { MetaLeadsTable } from './components/MetaLeadsTable';
import { CampaignAggregate, DayBucket } from './metaSelectors';
import {
  Campaign,
  CampaignPerformanceRow,
  IntegrationAccountStatus,
  LeadSummaryResponse,
  LeadTimeseriesPoint,
  MarketingLead,
  getCampaignPerformance,
  getIntegrationStatus,
  getLeadSummary,
  getLeadTimeseries,
  getMarketingLeadCampaigns,
  getMarketingLeads,
} from '../../../services/marketingLeads';

const DATE_FORMAT = 'YYYY-MM-DD';

// The leads TABLE at the bottom asks the server for every lead in the
// selected range (the list endpoint now takes from/to and is scoped to the
// connected account server-side). Every NUMBER on this screen — KPIs, chart,
// campaign rows — comes from the server too, so nothing here is capped by
// what the table happened to fetch. 500 is the validator's ceiling.
const LEAD_FETCH_LIMIT = 500;

const defaultRange = () => ({
  from: dayjs().subtract(29, 'day').format(DATE_FORMAT),
  to: dayjs().format(DATE_FORMAT),
});

const escapeCsvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

export type AdsPlatform = 'META' | 'GOOGLE';

interface PlatformAdsScreenProps {
  platform: AdsPlatform;
}

/**
 * One screen serving both /leads/meta and /leads/google. The two platforms
 * present identical information — connection state, KPIs, lead performance,
 * campaigns, leads — so duplicating the screen would guarantee they drift.
 */
const PlatformAdsScreen: React.FC<PlatformAdsScreenProps> = ({ platform }) => {
  const navigate = useNavigate();
  const label = platformMeta[platform].label;

  const [range, setRange] = useState(defaultRange);
  const [integration, setIntegration] = useState<IntegrationAccountStatus | null>(null);
  const [summary, setSummary] = useState<LeadSummaryResponse | null>(null);
  const [allLeads, setAllLeads] = useState<MarketingLead[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [timeseries, setTimeseries] = useState<LeadTimeseriesPoint[]>([]);
  const [performance, setPerformance] = useState<CampaignPerformanceRow[]>([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  // The range is widened to cover the stored data at most once per visit, so
  // an admin who then narrows it isn't fought by the effect on every refetch.
  const autoRangedRef = useRef(false);

  const fetchIntegration = useCallback(async () => {
    try {
      const result = await getIntegrationStatus();
      setIntegration(result[platform]);
    } catch (error) {
      console.error(`Failed to load ${platform} integration status:`, error);
      setIntegration(null);
    }
  }, [platform]);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await getLeadSummary({ from: range.from, to: range.to });
      setSummary(result);
    } catch (error) {
      console.error('Failed to load lead summary:', error);
      setSummary(null);
    }
  }, [range.from, range.to]);

  const fetchLeads = useCallback(async () => {
    try {
      setLoadingLeads(true);
      const result = await getMarketingLeads({
        page: 1,
        limit: LEAD_FETCH_LIMIT,
        source: platform,
        from: range.from,
        to: range.to,
      });
      setAllLeads(result.data);
      setLeadsTotal(result.total);
    } catch (error) {
      console.error(`Failed to load ${platform} leads:`, error);
      setAllLeads([]);
      setLeadsTotal(0);
    } finally {
      setLoadingLeads(false);
    }
  }, [platform, range.from, range.to]);

  useEffect(() => {
    fetchIntegration();
    fetchLeads();
    getMarketingLeadCampaigns()
      .then((all) => setCampaigns(all.filter((c) => c.platform === platform)))
      .catch((error) => {
        console.error('Failed to load campaigns:', error);
        setCampaigns([]);
      });
  }, [fetchIntegration, fetchLeads, platform]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoadingAnalytics(true);
      const [points, rows] = await Promise.all([
        getLeadTimeseries({ from: range.from, to: range.to, platform }),
        getCampaignPerformance({ from: range.from, to: range.to, platform }),
      ]);
      setTimeseries(points);
      setPerformance(rows);
    } catch (error) {
      console.error(`Failed to load ${platform} analytics:`, error);
      setTimeseries([]);
      setPerformance([]);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [platform, range.from, range.to]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const coverage = summary?.coverage?.[platform] || null;
  const currency = summary?.currency || 'GBP';

  // The earliest thing stored for this platform, leads or spend.
  const earliestStored = useMemo(() => {
    const candidates = [coverage?.leads.firstAt, coverage?.spend.firstAt].filter(Boolean) as string[];
    if (!candidates.length) return null;
    return candidates.map((iso) => dayjs(iso).format(DATE_FORMAT)).sort()[0];
  }, [coverage]);

  /**
   * Open on a range that actually covers the data.
   *
   * A history import pulls 90 days of leads and a year of spend, but this
   * screen defaulted to the trailing 30 days with nothing on it saying so —
   * so it showed a confident subset while the Leads overview (which applies
   * no date filter at all) showed everything, and the two disagreed. Widening
   * once on arrival makes the screens agree by default; the picker still
   * narrows it, and the banner below covers whatever falls outside.
   */
  useEffect(() => {
    if (autoRangedRef.current || !earliestStored) return;
    autoRangedRef.current = true;
    if (earliestStored < range.from) {
      setRange((r) => ({ ...r, from: earliestStored }));
    }
  }, [earliestStored, range.from]);

  const showAllData = useCallback(() => {
    if (!earliestStored) return;
    setRange({ from: earliestStored, to: dayjs().format(DATE_FORMAT) });
  }, [earliestStored]);

  // Leads stored for this platform that the selected range excludes. The
  // server counts both — `leadsTotal` is the range, `coverage.leads.total`
  // is everything — so this is never guesswork from a truncated page.
  const leadsOutsideRange = Math.max(0, (coverage?.leads.total || 0) - leadsTotal);

  // Server-filtered already; re-filtering here on the browser's local dates
  // against the server's UTC day boundaries silently dropped leads received
  // late in a UTC day.
  const leadsInRange = allLeads;

  // Chart + top-campaigns keep their existing prop shapes; the data now
  // comes from the server so it covers every lead, not the first 100.
  const buckets = useMemo<DayBucket[]>(
    () => timeseries.map((p) => ({ date: p.date, count: p.leads })),
    [timeseries],
  );

  const campaignAggregates = useMemo<CampaignAggregate[]>(
    () => performance.map((r) => ({ campaignName: r.campaignName, leads: r.leads, qualified: r.qualified })),
    [performance],
  );

  const platformMetrics = summary?.platforms?.[platform] || null;

  // All five KPIs from the server summary, so this screen and the Leads
  // overview can never show different numbers for the same range.
  const kpiInput = useMemo(
    () => ({
      totalLeads: platformMetrics?.leads ?? 0,
      qualified: platformMetrics?.qualifiedLeads ?? 0,
      contacted: platformMetrics?.callsReceived ?? 0,
      costPerLead: platformMetrics?.costPerLead || 0,
      amountSpent: platformMetrics?.amountSpent || 0,
      changePct: platformMetrics?.changePct || null,
      currency,
      // No campaign-days of spend stored for this range = nothing measured,
      // so the money tiles show "—" instead of a zero nobody can act on.
      hasSpendData: (platformMetrics?.spendDays ?? 0) > 0,
    }),
    [platformMetrics, currency],
  );

  const handleExport = () => {
    const header = ['Name', 'Phone', 'Email', 'Campaign', 'Received On', 'Status'];
    const rows = leadsInRange.map((lead) => [
      lead.name,
      lead.phoneNumber,
      lead.email,
      lead.campaignName || 'Uncategorized',
      dayjs(lead.receivedOn).format('YYYY-MM-DD HH:mm'),
      lead.status,
    ]);

    const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${platform.toLowerCase()}-leads-${range.from}-to-${range.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 3 }}>
      <PageHeaderBar showDivider>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate('/leads')}
            aria-label="Back to lead management"
            sx={{ color: '#e2e8f0' }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box>
            <Typography sx={appShellHeaderTitleSx}>{label}</Typography>
            <Typography sx={{ fontSize: 12, color: dashboardTokens.textSecondary, mt: 0.25 }}>
              See how your {label} campaigns are bringing in leads
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <DateRangePopover from={range.from} to={range.to} onChange={setRange} />
          <IconButton
            size="small"
            onClick={handleExport}
            aria-label={`Export ${label} leads as CSV`}
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#181818',
              border: `1px solid ${dashboardTokens.border}`,
              color: '#e2e8f0',
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#232323' },
            }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <AdminAvatar />
        </Box>
      </PageHeaderBar>

      <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <ConnectionCard platform={platform} integration={integration} />

        {leadsOutsideRange > 0 && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ bgcolor: '#1c1c1c', color: '#e2e8f0', borderColor: dashboardTokens.border }}
            action={
              <Button size="small" onClick={showAllData} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Show all
              </Button>
            }
          >
            {leadsOutsideRange} more {label} lead{leadsOutsideRange === 1 ? '' : 's'} fall outside{' '}
            {dayjs(range.from).format('D MMM YYYY')} – {dayjs(range.to).format('D MMM YYYY')}. Every
            figure on this screen covers the selected range only.
          </Alert>
        )}

        <MetaKpiTiles input={kpiInput} />

        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
          <LeadPerformanceChart buckets={buckets} loading={loadingAnalytics} />
          <TopCampaigns campaigns={campaignAggregates} loading={loadingAnalytics} />
        </Box>

        <MetaCampaignsTable
          rows={performance}
          campaigns={campaigns}
          loading={loadingAnalytics}
          platformLabel={label}
          currency={currency}
        />

        <MetaLeadsTable
          leads={leadsInRange}
          loading={loadingLeads}
          platformLabel={label}
          total={leadsTotal}
          rangeLabel={`${dayjs(range.from).format('D MMM YYYY')} – ${dayjs(range.to).format('D MMM YYYY')}`}
        />
      </Box>
    </Box>
  );
};

export const MetaAdsScreen = withComponentAuth(() => (
  <PlatformAdsScreen platform="META" />
));

export const GoogleAdsScreen = withComponentAuth(() => (
  <PlatformAdsScreen platform="GOOGLE" />
));

export default MetaAdsScreen;
